import { Fragment, useId, useState } from 'react';
import { ChevronRightIcon, CircleDashedIcon } from 'lucide-react';

import type { PeriodBounds } from '@/lib/api/generated/model';
import { cn } from '@/lib/utils';
import { ListCard } from '@/shared/components/ListCard';
import { LIST_ROW, LIST_ROWS } from '@/shared/components/listRow';
import { NoteChip } from '@/shared/components/NoteChip';
import {
  DISCLOSURE_CHEVRON,
  DISCLOSURE_TRIGGER,
} from '@/shared/components/disclosure';
import {
  matchesMapRow,
  runsCount,
  splitMapRows,
  type MapRow,
} from '../modelsLedger';
import {
  UNMEASURED_HINT,
  idleWorkflowsLine,
  unmeasuredChipText,
} from '../aiModelsCopy';
import { ProfileLegend, type LegendProfile } from './ProfileLegend';
import { StepChip } from './StepChip';
import { WorkflowNameLink } from './WorkflowNameLink';

/** Steps a collapsed row shows before "+N steps" (spec 252): one line on a desktop. */
export const STEPS_IN_LINE = 4;

/**
 * Which step runs on which model (spec 233 US3, service 173): one row per workflow,
 * every step in order with an arrow between each pair, each the one step chip
 * (spec 234): an AI step leads with its profile's dot; an AI step with no known
 * profile says it runs on the default model; an approval or a linked workflow is
 * muted and runs none. Each row ends with its runs. The legend lifts one profile and
 * dims everything else.
 *
 * Read in one line per workflow (spec 252): the workflows that ran come first, by
 * runs; a long one ends in "+N steps" and opens in place; the ones that did not run
 * fold into one row that opens to list them.
 */
export function StepMap({
  rows,
  legend,
  workspaceId,
  period,
}: {
  rows: MapRow[];
  legend: LegendProfile[];
  workspaceId: string;
  period: PeriodBounds;
}) {
  const [query, setQuery] = useState('');
  const [pressed, setPressed] = useState<string | null>(null);
  const [idleOpen, setIdleOpen] = useState(false);
  const idleId = useId();
  const shown = rows.filter((row) => matchesMapRow(row, query));
  const names = new Map(legend.map((profile) => [profile.key, profile]));
  // A search shows every match where it falls; the fold is for reading the whole map.
  const searching = query.trim() !== '';
  const { ran, idle } = searching
    ? { ran: shown, idle: [] as MapRow[] }
    : splitMapRows(shown);
  const row = (mapRow: MapRow) => (
    <MapRowView
      key={mapRow.workflowId}
      row={mapRow}
      names={names}
      pressed={pressed}
      workspaceId={workspaceId}
    />
  );
  return (
    <ListCard
      title="Workflows"
      count={rows.length}
      noun={['workflow', 'workflows']}
      description="Every step in order. Steps that run an AI model carry their profile’s dot; approvals run none."
      action={
        legend.length > 0 ? (
          <ProfileLegend
            profiles={legend}
            pressed={pressed}
            onPress={setPressed}
          />
        ) : undefined
      }
      search={{
        value: query,
        onChange: setQuery,
        placeholder: 'Search by workflow name',
        label: 'Search workflows',
      }}
      shown={shown.length}
      total={rows.length}
      state={rows.length === 0 ? 'empty' : 'idle'}
      empty={{ description: 'No workflow uses one of these profiles now.' }}
      noResults={{
        description: `No workflows match “${query.trim()}”.`,
        onClear: () => {
          setQuery('');
        },
      }}
    >
      <ul aria-label="Workflows" className={LIST_ROWS}>
        {ran.map(row)}
        {idle.length > 0 ? (
          <li className={cn(LIST_ROW, 'flex-col items-stretch')}>
            <button
              type="button"
              aria-expanded={idleOpen}
              aria-controls={idleId}
              onClick={() => {
                setIdleOpen((was) => !was);
              }}
              className={cn(DISCLOSURE_TRIGGER, 'self-start')}
            >
              <ChevronRightIcon
                aria-hidden="true"
                className={DISCLOSURE_CHEVRON}
              />
              {idleWorkflowsLine(idle.length, period)}
            </button>
            <ul
              id={idleId}
              hidden={!idleOpen}
              aria-label="Workflows that did not run"
              className={cn(LIST_ROWS, 'mt-2')}
            >
              {idle.map(row)}
            </ul>
          </li>
        ) : null}
      </ul>
    </ListCard>
  );
}

/** One workflow's line: its name, its steps on one line, then its runs. */
function MapRowView({
  row,
  names,
  pressed,
  workspaceId,
}: {
  row: MapRow;
  names: Map<string, LegendProfile>;
  pressed: string | null;
  workspaceId: string;
}) {
  const [open, setOpen] = useState(false);
  const stepsId = useId();
  const hidden = open ? 0 : Math.max(row.steps.length - STEPS_IN_LINE, 0);
  const steps = hidden > 0 ? row.steps.slice(0, STEPS_IN_LINE) : row.steps;
  const opens = row.steps.length > STEPS_IN_LINE;
  return (
    <li className={LIST_ROW}>
      <div
        data-map-row=""
        onClick={(event) => {
          // The name is a link to the workflow; the rest of the row opens its steps.
          if (!opens || (event.target as HTMLElement).closest('a, button'))
            return;
          setOpen((was) => !was);
        }}
        className={cn(
          'flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-4',
          opens && 'cursor-pointer',
        )}
      >
        <div className="min-w-0 shrink-0 sm:w-52">
          <WorkflowNameLink
            workspaceId={workspaceId}
            workflowKey={row.key}
            workflowName={row.name}
            draft={row.draft}
          />
        </div>
        <ol
          id={stepsId}
          aria-label={`Steps of ${row.name}`}
          className={cn(
            'flex min-w-0 flex-1 items-center gap-1.5',
            open ? 'flex-wrap' : 'flex-wrap sm:flex-nowrap',
          )}
        >
          {steps.map((step, index) => {
            const profile = step.profileKey
              ? names.get(step.profileKey)
              : undefined;
            return (
              <Fragment key={step.id}>
                {index > 0 ? (
                  <li
                    aria-hidden="true"
                    data-step-arrow=""
                    className="text-muted-foreground text-xs"
                  >
                    →
                  </li>
                ) : null}
                <li className="min-w-0">
                  <StepChip
                    kind={
                      step.kind === 'profile' && !profile
                        ? 'default'
                        : step.kind
                    }
                    name={step.name}
                    slot={profile?.slot}
                    idle={profile?.idle}
                    profileName={profile?.name}
                    profileKey={step.profileKey ?? undefined}
                    unpublished={step.unpublished}
                    dimmed={
                      pressed !== null &&
                      !(step.kind === 'profile' && step.profileKey === pressed)
                    }
                  />
                </li>
              </Fragment>
            );
          })}
          {opens ? (
            <li>
              <button
                type="button"
                aria-expanded={open}
                aria-controls={stepsId}
                aria-label={
                  open
                    ? `Show fewer steps of ${row.name}`
                    : `Show all ${String(row.steps.length)} steps of ${row.name}`
                }
                onClick={() => {
                  setOpen((was) => !was);
                }}
                className="touch-target text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded-sm text-xs whitespace-nowrap focus-visible:ring-3 focus-visible:outline-none"
              >
                {open ? 'Fewer steps' : `+${String(hidden)} steps`}
              </button>
            </li>
          ) : null}
        </ol>
        <div className="flex shrink-0 flex-wrap items-center gap-2 sm:ms-auto sm:justify-end">
          <span className="text-muted-foreground text-xs tabular-nums">
            {runsCount(row.runs)}
          </span>
          {row.unmeasured > 0 ? (
            <NoteChip
              icon={CircleDashedIcon}
              text={unmeasuredChipText(row.unmeasured)}
              title={UNMEASURED_HINT}
            />
          ) : null}
        </div>
      </div>
    </li>
  );
}
